/**
 * AiAssistantContext — состояние панели ИИ-помощника.
 *
 * Что здесь живёт:
 *  • открытость панели (кнопка в верхней панели переключает `open`);
 *  • session_id — он же хранится в localStorage, поэтому закрытие панели или
 *    перезагрузка страницы не обнуляют диалог: лента берётся из БД;
 *  • лента карточек и этап работы (слушаю / распознаю / думаю);
 *  • непрерывная запись: поток не прерывается, каждая законченная фраза
 *    распознаётся и копится, а после паузы уходит в модель одним запросом.
 *
 * Модуль независим от внутреннего чата сотрудников (modules/chat) и от прежних
 * ИИ-чатов медкарты.
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import GetData from "../../services/getData";
import { fetchAiProgress } from "../../services/aiProgress";
import useVoiceStream from "./useVoiceStream";

/** Этапы работы панели. */
export const STAGE_IDLE = "idle";
export const STAGE_LISTENING = "listening";
export const STAGE_TRANSCRIBING = "transcribing";
export const STAGE_THINKING = "thinking";

/** Ключ localStorage с идентификатором сессии. */
const SESSION_KEY = "mis.ai.session";
/** Сколько последних карточек держим в памяти клиента. */
const MAX_CLIENT_FEED = 30;
/** Пауза после фразы, через которую запрос уходит в модель. */
const COMMIT_DELAY_MS = 1300;
/** Как часто панель спрашивает у сервера, что агент делает сейчас. */
const PROGRESS_POLL_MS = 700;

/**
 * Идентификатор запроса к агенту.
 *
 * По нему сервер отдаёт живой прогресс (/api/progress) и пишет подробный
 * журнал в файл — по нему же потом можно найти запрос в логах.
 */
function newRequestId() {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID().replace(/-/g, "").slice(0, 24);
        }
    } catch (err) {
        // crypto недоступен — падаем на время+случайное
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

const AiAssistantContext = createContext({
    open: false,
    setOpen: () => {},
    toggle: () => {},
    sessionId: "",
    feed: [],
    stage: STAGE_IDLE,
    notice: "",
    lastError: "",
    progress: null,
    patientId: 0,
    patient: null,
    listening: false,
    level: 0,
    liveText: "",
    audioError: "",
    sendText: async () => {},
    confirmAction: async () => false,
    startListening: async () => {},
    stopListening: async () => {},
    resetSession: async () => {},
    setPatientId: () => {},
});

export function useAiAssistant() {
    return useContext(AiAssistantContext);
}

const isError = (data) => data instanceof Error;
const errorText = (data, fallback) => {
    if (isError(data)) return data.message || fallback;
    if (!data) return fallback;
    return data.message || data?.response?.data?.message || fallback;
};

function readSession() {
    try {
        return String(localStorage.getItem(SESSION_KEY) || "");
    } catch (err) {
        return "";
    }
}

function writeSession(value) {
    try {
        if (value) {
            localStorage.setItem(SESSION_KEY, value);
        } else {
            localStorage.removeItem(SESSION_KEY);
        }
    } catch (err) {
        // localStorage недоступен — история просто не восстановится
    }
}

/**
 * Действующий пациент сессии (единственный, с кем работает агент).
 * Сервер отдаёт его в `patient` (новый формат) или в `state.active_patient`.
 */
function normalizePatient(result) {
    const source = result?.patient || result?.state?.active_patient || null;
    const id = Number(source?.id) || 0;
    if (!id) return null;
    return { id, fio: String(source.fio || "") };
}

function normalizeFeed(list) {
    if (!Array.isArray(list)) return [];
    return list
        .filter((item) => item && item.role !== "transcript")
        .slice(-MAX_CLIENT_FEED)
        .map((item) => ({
            id: Number(item.id) || 0,
            role: String(item.role || ""),
            content: String(item.content || ""),
            blocks: Array.isArray(item.blocks) ? item.blocks : [],
            navigate: String(item.navigate || ""),
            navigateTitle: String(item.navigate_title || ""),
            date: String(item.date || ""),
        }));
}

export function AiAssistantProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(readSession);
    const [feed, setFeed] = useState([]);
    const [stage, setStage] = useState(STAGE_IDLE);
    const [notice, setNotice] = useState("");
    const [lastError, setLastError] = useState("");
    const [patientId, setPatientId] = useState(0);
    const [patient, setPatient] = useState(null);
    const [liveText, setLiveText] = useState("");
    const [navigateRequest, setNavigateRequest] = useState(null);
    const [progress, setProgress] = useState(null);

    const sessionRef = useRef(sessionId);
    const loadingRef = useRef(false);
    const stageRef = useRef(STAGE_IDLE);
    const liveTextRef = useRef("");
    const pendingRef = useRef(0);
    const commitTimerRef = useRef(0);
    const queueRef = useRef(Promise.resolve());
    const commitRef = useRef(null);
    const pollRef = useRef(0);

    /**
     * Живой прогресс: пока сервер выполняет запрос, панель показывает шаги.
     *
     * Основной POST отвечает только в конце, поэтому «что делается сейчас»
     * спрашиваем отдельно и часто — это чтение памяти процесса, без базы.
     */
    const beginProgress = useCallback((requestId) => {
        window.clearInterval(pollRef.current);
        setProgress({ id: requestId, phase: "plan", label: "", steps: [] });
        pollRef.current = window.setInterval(async () => {
            const live = await fetchAiProgress(requestId);
            if (!live) return;
            setProgress({
                id: requestId,
                phase: String(live.phase || ""),
                label: String(live.label || ""),
                steps: Array.isArray(live.steps) ? live.steps : [],
            });
        }, PROGRESS_POLL_MS);
    }, []);

    const endProgress = useCallback(() => {
        window.clearInterval(pollRef.current);
        pollRef.current = 0;
        setProgress(null);
    }, []);

    useEffect(
        () => () => {
            window.clearInterval(pollRef.current);
        },
        [],
    );

    useEffect(() => {
        sessionRef.current = sessionId;
    }, [sessionId]);

    useEffect(() => {
        stageRef.current = stage;
    }, [stage]);

    /** Лента и id сессии подтягиваются при первом открытии панели. */
    const ensureSession = useCallback(async () => {
        if (loadingRef.current) return sessionRef.current;
        loadingRef.current = true;
        try {
            const data = await GetData({
                module: "ai_assistant",
                block: "history",
                session_id: sessionRef.current,
            });
            if (isError(data) || !data?.result) return sessionRef.current;
            const result = data.result;
            const nextId = String(result.session_id || "");
            if (nextId) {
                sessionRef.current = nextId;
                setSessionId(nextId);
                writeSession(nextId);
            }
            setFeed(normalizeFeed(result.messages));
            setPatient(normalizePatient(result));
            return nextId;
        } catch (err) {
            return sessionRef.current;
        } finally {
            loadingRef.current = false;
        }
    }, []);

    /** При старте приложения сразу поднимаем историю сессии из БД. */
    useEffect(() => {
        ensureSession();
    }, [ensureSession]);

    /**
     * Общий разбор ответа сервера: лента, карточки, переход на страницу.
     */
    const applyResult = useCallback((data) => {
        if (isError(data)) {
            setLastError(data.message || "Нет связи с сервером.");
            return false;
        }
        if (!data) {
            setLastError("Пустой ответ сервера.");
            return false;
        }
        const result = data.result;
        if (!result) {
            setLastError(errorText(data, "Не удалось получить ответ."));
            return false;
        }
        if (result.session_id) {
            sessionRef.current = String(result.session_id);
            setSessionId(String(result.session_id));
            writeSession(String(result.session_id));
        }
        if (Array.isArray(result.messages)) {
            setFeed(normalizeFeed(result.messages));
        }
        setPatient(normalizePatient(result));
        setLastError("");
        if (result.navigate) {
            setNavigateRequest({
                path: String(result.navigate),
                title: String(result.navigate_title || ""),
                at: Date.now(),
            });
        }
        return true;
    }, []);

    const sendText = useCallback(
        async (text) => {
            const value = String(text || "").trim();
            if (!value) return;
            const requestId = newRequestId();
            setStage(STAGE_THINKING);
            setNotice("");
            beginProgress(requestId);
            try {
                const data = await GetData(
                    {
                        module: "ai_assistant",
                        block: "chat",
                        session_id: sessionRef.current,
                        id_patient: patientId,
                        text: value,
                        request_id: requestId,
                    },
                    null,
                    { timeout: 180000 },
                );
                applyResult(data);
            } catch (err) {
                setLastError(err?.message || "Нет связи с сервером.");
            } finally {
                endProgress();
                setStage(STAGE_IDLE);
            }
        },
        [applyResult, beginProgress, endProgress, patientId],
    );

    /**
     * Кнопка карточки подтверждения. Уходит прямо на сервер с токеном
     * подготовленного действия: модель здесь не участвует, иначе на каждое
     * «подтверждаю» она могла снова показать ту же карточку.
     */
    const confirmAction = useCallback(
        async (block, agreed = true) => {
            const token = String(block?.token || "");
            const requestId = newRequestId();
            setStage(STAGE_THINKING);
            setNotice("");
            beginProgress(requestId);
            try {
                const data = await GetData(
                    {
                        module: "ai_assistant",
                        block: "confirm",
                        session_id: sessionRef.current,
                        id_patient: patientId,
                        token,
                        decision: agreed ? "confirm" : "cancel",
                        request_id: requestId,
                    },
                    null,
                    { timeout: 60000 },
                );
                applyResult(data);
                return !isError(data);
            } catch (err) {
                setLastError(err?.message || "Нет связи с сервером.");
                return false;
            } finally {
                endProgress();
                setStage(STAGE_IDLE);
            }
        },
        [applyResult, beginProgress, endProgress, patientId],
    );

    /** Один фрагмент речи: распознаём и копим текст до паузы. */
    const transcribeSegment = useCallback(async (blob) => {
        if (!blob) return "";
        setStage(STAGE_TRANSCRIBING);
        try {
            const file = new File([blob], "segment.wav", {
                type: blob.type || "audio/wav",
            });
            const data = await GetData(
                {
                    module: "ai_assistant",
                    block: "transcribe",
                    session_id: sessionRef.current,
                },
                file,
                { timeout: 120000 },
            );
            if (isError(data) || !data?.result) return "";
            if (data.result.session_id) {
                sessionRef.current = String(data.result.session_id);
                setSessionId(String(data.result.session_id));
                writeSession(String(data.result.session_id));
            }
            return String(data.result.transcript || "").trim();
        } catch (err) {
            return "";
        } finally {
            setStage((prev) => (prev === STAGE_TRANSCRIBING ? STAGE_LISTENING : prev));
        }
    }, []);

    /** Весь накопленный текст уходит в модель одним запросом. */
    const commitUtterance = useCallback(async () => {
        const text = liveTextRef.current.trim();
        if (!text) return;
        if (stageRef.current === STAGE_THINKING) {
            // Модель ещё отвечает — переносим отправку на потом.
            window.clearTimeout(commitTimerRef.current);
            commitTimerRef.current = window.setTimeout(() => {
                void commitRef.current?.();
            }, 700);
            return;
        }
        liveTextRef.current = "";
        setLiveText("");
        setStage(STAGE_THINKING);
        setNotice("");
        try {
            const data = await GetData(
                {
                    module: "ai_assistant",
                    block: "chat",
                    session_id: sessionRef.current,
                    id_patient: patientId,
                    audio: 1,
                },
                null,
                { timeout: 240000 },
            );
            applyResult(data);
        } catch (err) {
            setLastError(err?.message || "Нет связи с сервером.");
        } finally {
            setStage(STAGE_IDLE);
        }
    }, [applyResult, patientId]);

    useEffect(() => {
        commitRef.current = commitUtterance;
    }, [commitUtterance]);

    /** Пауза в речи -> отправка. Короткое продолжение сливается в тот же запрос. */
    const scheduleCommit = useCallback(() => {
        window.clearTimeout(commitTimerRef.current);
        commitTimerRef.current = window.setTimeout(() => {
            if (pendingRef.current > 0) {
                scheduleCommit();
                return;
            }
            void commitRef.current?.();
        }, COMMIT_DELAY_MS);
    }, []);

    const handleSegment = useCallback(
        (blob) => {
            if (!blob) return;
            pendingRef.current += 1;
            queueRef.current = queueRef.current
                .then(async () => {
                    const transcript = await transcribeSegment(blob);
                    if (transcript) {
                        liveTextRef.current = liveTextRef.current
                            ? `${liveTextRef.current} ${transcript}`
                            : transcript;
                        setLiveText(liveTextRef.current);
                    }
                })
                .catch(() => null)
                .finally(() => {
                    pendingRef.current = Math.max(0, pendingRef.current - 1);
                    scheduleCommit();
                });
        },
        [transcribeSegment, scheduleCommit],
    );

    /** Пользователь снова заговорил — отложенную отправку отменяем. */
    const handleSpeechStart = useCallback(() => {
        window.clearTimeout(commitTimerRef.current);
    }, []);

    const stream = useVoiceStream({
        onSegment: handleSegment,
        onSpeechStart: handleSpeechStart,
    });

    const startListening = useCallback(async () => {
        if (stageRef.current === STAGE_THINKING) return;
        setLastError("");
        liveTextRef.current = "";
        setLiveText("");
        await stream.start();
        setStage(STAGE_LISTENING);
    }, [stream]);

    const stopListening = useCallback(async () => {
        await stream.stop();
        window.clearTimeout(commitTimerRef.current);
        await queueRef.current;
        setStage((prev) => (prev === STAGE_LISTENING ? STAGE_IDLE : prev));
        await commitRef.current?.();
    }, [stream]);

    const resetSession = useCallback(async () => {
        setStage(STAGE_IDLE);
        setNotice("");
        setLastError("");
        window.clearTimeout(commitTimerRef.current);
        liveTextRef.current = "";
        setLiveText("");
        try {
            const data = await GetData({
                module: "ai_assistant",
                block: "reset",
                session_id: sessionRef.current,
            });
            if (!isError(data) && data?.result?.session_id) {
                sessionRef.current = String(data.result.session_id);
                setSessionId(String(data.result.session_id));
                writeSession(String(data.result.session_id));
            }
            setFeed([]);
            setPatient(null);
        } catch (err) {
            setFeed([]);
            setPatient(null);
        }
    }, []);

    const toggle = useCallback(() => setOpen((prev) => !prev), []);

    const takeNavigate = useCallback(() => {
        setNavigateRequest(null);
        return null;
    }, []);

    const value = useMemo(
        () => ({
            open,
            setOpen,
            toggle,
            sessionId,
            feed,
            stage,
            notice,
            setNotice,
            lastError,
            setLastError,
            progress,
            patientId,
            patient,
            setPatientId,
            listening: stream.active,
            level: stream.level,
            audioError: stream.error,
            liveText,
            sendText,
            confirmAction,
            startListening,
            stopListening,
            resetSession,
            navigateRequest,
            takeNavigate,
            refresh: ensureSession,
        }),
        [
            open,
            sessionId,
            feed,
            stage,
            notice,
            lastError,
            progress,
            patientId,
            patient,
            stream.active,
            stream.level,
            stream.error,
            liveText,
            sendText,
            confirmAction,
            startListening,
            stopListening,
            resetSession,
            navigateRequest,
            takeNavigate,
            ensureSession,
            toggle,
        ],
    );

    return (
        <AiAssistantContext.Provider value={value}>
            {children}
        </AiAssistantContext.Provider>
    );
}

export default AiAssistantProvider;
